import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/Store';
import {
  fetchAgentReceipts,
  AgentReceiptDto,
} from '../../redux/thunks/agentThunks';
import {
  selectAgentReceipts,
  selectAgentReceiptsStatus,
  selectAgentReceiptsError,
  selectAgentReceiptsNextCursor,
} from '../../redux/selectors/agentSelectors';
import { LoadingSpinner } from '../LoadingSpinner';
import { Button } from '../Button/Button';
import './AgentReceiptList.scss';

interface AgentReceiptListProps {
  providerAgentUserId: string;
  /** Cap the first page size (defaults to server's 50). */
  pageSize?: number;
}

/**
 * Ring 3 Phase 1 read-only receipt listing for a given provider agent.
 *
 * Fetches on mount + when providerAgentUserId changes. Cursor
 * pagination via a "Load more" button — matches the pattern used
 * by the Discover feed. Each row shows:
 *
 *   [OUTCOME] [STATE] capability — payment_amount asset — relative time
 *   requester (wallet or agent handle) → provider endpoint
 *
 * Renders nothing if the agent has never received a receipt (parent
 * decides whether to render a section header + empty message).
 */
const AgentReceiptList: React.FC<AgentReceiptListProps> = ({
  providerAgentUserId,
  pageSize,
}) => {
  const dispatch = useAppDispatch();
  const receipts = useAppSelector(selectAgentReceipts(providerAgentUserId));
  const status = useAppSelector(selectAgentReceiptsStatus(providerAgentUserId));
  const error = useAppSelector(selectAgentReceiptsError(providerAgentUserId));
  const nextCursor = useAppSelector(
    selectAgentReceiptsNextCursor(providerAgentUserId),
  );

  useEffect(() => {
    const args: {
      providerAgentUserId: string;
      limit?: number;
    } = { providerAgentUserId };
    if (pageSize !== undefined) args.limit = pageSize;
    dispatch(fetchAgentReceipts(args));
  }, [dispatch, providerAgentUserId, pageSize]);

  const handleLoadMore = () => {
    if (!nextCursor || status === 'loading_more') return;
    const args: {
      providerAgentUserId: string;
      cursor: string;
      limit?: number;
    } = { providerAgentUserId, cursor: nextCursor };
    if (pageSize !== undefined) args.limit = pageSize;
    dispatch(fetchAgentReceipts(args));
  };

  if (status === 'loading' && receipts.length === 0) {
    return (
      <div className="agent-receipt-list agent-receipt-list--loading">
        <LoadingSpinner size="small" message="Loading receipts…" />
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="agent-receipt-list agent-receipt-list--error" role="alert">
        {error ?? 'Failed to load receipts.'}
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <p className="agent-receipt-list__empty">
        No receipts recorded yet.
      </p>
    );
  }

  return (
    <div className="agent-receipt-list">
      <ul className="agent-receipt-list__items">
        {receipts.map((r) => (
          <ReceiptRow key={r.id} r={r} />
        ))}
      </ul>
      {nextCursor && (
        <div className="agent-receipt-list__loadmore">
          <Button
            variant="secondary"
            onClick={handleLoadMore}
            disabled={status === 'loading_more'}
          >
            {status === 'loading_more' ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
};

// --- Row helpers ---------------------------------------------------

const ReceiptRow: React.FC<{ r: AgentReceiptDto }> = ({ r }) => {
  return (
    <li className="agent-receipt-list__item">
      <div className="agent-receipt-list__row">
        <span
          className={`agent-receipt-list__outcome agent-receipt-list__outcome--${r.outcome}`}
        >
          {r.outcome}
        </span>
        <span
          className={`agent-receipt-list__state agent-receipt-list__state--${r.state}`}
        >
          {stateLabel(r.state)}
        </span>
        {r.capability && (
          <span className="agent-receipt-list__capability">{r.capability}</span>
        )}
        <span className="agent-receipt-list__payment mono">
          {formatAmount(r.paymentAmountSmallest, r.paymentAsset)}
        </span>
        <span className="agent-receipt-list__time" title={new Date(r.invokedAt).toISOString()}>
          {relativeTime(r.invokedAt)}
        </span>
      </div>
      <div className="agent-receipt-list__sub">
        <RequesterLabel r={r} />
      </div>
    </li>
  );
};

// Requester side. Preference order for what to render:
//   1. If requesterAgentUsername is present → linked `@handle` (issue #569)
//   2. Else if requesterAgentUserId is present (agent whose account was
//      deleted after the receipt landed) → 'agent (deleted)'
//   3. Else if requesterWallet is present → shortened wallet address
//   4. Otherwise → 'anonymous'
const RequesterLabel: React.FC<{ r: AgentReceiptDto }> = ({ r }) => {
  if (r.requesterAgentUsername) {
    return (
      <Link
        to={`/profile/${r.requesterAgentUsername}`}
        className="agent-receipt-list__requester"
      >
        @{r.requesterAgentUsername}
      </Link>
    );
  }
  if (r.requesterAgentUserId) {
    return (
      <span className="agent-receipt-list__requester agent-receipt-list__requester--deleted">
        agent (deleted)
      </span>
    );
  }
  if (r.requesterWallet) {
    return (
      <span className="agent-receipt-list__requester mono">
        {shorten(r.requesterWallet)}
      </span>
    );
  }
  return (
    <span className="agent-receipt-list__requester">anonymous</span>
  );
};

// --- Formatters ----------------------------------------------------

function stateLabel(state: string): string {
  // The DB enum uses snake_case; UI reads better as space-separated.
  return state.replace(/_/g, ' ');
}

/**
 * Payment display. paymentAmountSmallest is smallest-unit as a
 * decimal string. Divides by known decimals for KLV/KLYX; falls
 * back to raw for unknown assets so we never lie about precision.
 */
function formatAmount(smallest: string, asset: string): string {
  const decimals = decimalsFor(asset);
  if (decimals === null) return `${smallest} ${asset}`;
  // Use BigInt to avoid precision loss on the smallest-unit string.
  const n = BigInt(smallest);
  const base = 10n ** BigInt(decimals);
  const whole = n / base;
  const frac = n % base;
  if (frac === 0n) return `${whole} ${asset}`;
  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole}.${fracStr} ${asset}`;
}

function decimalsFor(asset: string): number | null {
  const a = asset.toUpperCase();
  if (a === 'KLV') return 6;
  if (a === 'KLYX') return 8;
  return null;
}

function shorten(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-4)}`;
}

/**
 * Relative-time formatter. "3m ago", "2h ago", "5d ago", "Aug 12".
 * Falls back to a short date once we're past a week.
 */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export default AgentReceiptList;
